import React, { useEffect, useState } from "react";
import moment from "moment";

// External components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Translation
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";

// APIs
import getPhishing from "api/phishing/getPhishing";
import getPhishingScreenshot from "api/phishing/getPhishingScreenshot";

// MUI
import Icon from "@mui/material/Icon";
import { IconButton } from "@mui/material";
import { useMaterialUIController } from "context";

import SweetAlert from "react-bootstrap-sweetalert";
import phishingManaged from "api/phishing/phishingManaged";
import askTakedown from "api/phishing/askTakedown";
import updateTakeDownStatus from "api/phishing/updateTakeDownStatus";
import updateWhitelisted from "api/phishing/updateWhitelisted";

const PhishingTakeDownStatus = {
  NONE: 0,
  REQUESTED: 2,
  TAKEN_DOWN: 3,
  FALSE_POSITIVE: 4,
  FAILED: 5,
  NOT_PROCESSABLE: 6,
};

const PhishingDetails = () => {
  const [data, setData] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [popupSuccess, setPopupSuccess] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  // eslint-disable-next-line
  const [controller, _] = useMaterialUIController();
  const { user } = controller;

  const getData = async () => {
    try {
      const response = await getPhishing(id);
      setData(response);
    } catch (e) {
      console.log("error --->", e);
    }

    try {
      const blob = await getPhishingScreenshot(id);
      setScreenshot(blob);
    } catch (e) {
      setScreenshot(null);
    }
  };

  const handleAskTakedownBtn = async () => {
    try {
      await askTakedown(id);
      await getData();
    } catch (e) {
      console.log("error --->", e);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* back to the results */}
      <div style={{ marginBottom: "15px" }}>
        <Link className="back-to-results-link" to="/phishing/">
          {t("dataBreach.back_to_results")}
        </Link>
      </div>
      {/* Title */}
      <h2 className="page-title">{data?.victim?.name || data?.url || ""}</h2>

      {/* Box */}
      <div className="boxes-border-radius" style={{ background: "white", padding: "15px" }}>
        {data ? (
          <>
            <dl
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: "40px",
                rowGap: "10px",
                fontSize: "16px",
              }}
            >
              {/* Colonna 1 */}
              <div>
                <dt style={{ fontWeight: "600" }}>{t("phishing.title")}</dt>
                <dd style={{ marginBottom: "15px" }}>{data.title || "N/A"}</dd>

                <dt style={{ fontWeight: "600" }}>{t("phishing.first_seen")}</dt>
                <dd style={{ marginBottom: "15px" }}>
                  {data.firstSeen ? moment(data.firstSeen).format("DD/MM/YYYY") : "N/A"}
                </dd>

                {data.lastSeen && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.last_seen")}</dt>
                    <dd style={{ marginBottom: "15px" }}>
                      {moment(data.lastSeen).format("DD/MM/YYYY")}
                    </dd>
                  </>
                )}

                <dt style={{ fontWeight: "600" }}>{t("phishing.victim_name")}</dt>
                <dd style={{ marginBottom: "15px" }}>{data.victim?.name || "N/A"}</dd>

                <dt style={{ fontWeight: "600" }}>{t("phishing.victim_nation")}</dt>
                <dd style={{ marginBottom: "15px" }}>{data.victim?.country || "N/A"}</dd>

                <dt style={{ fontWeight: "600" }}>{t("phishing.domain")}</dt>
                <dd style={{ marginBottom: "15px" }}>{data.domain || "N/A"}</dd>

                {data.url && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.url")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.url}</dd>
                  </>
                )}

                {data.path && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.path")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.path}</dd>
                  </>
                )}

                {(data.city || data.countryCode) && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.geo_location")}</dt>
                    <dd style={{ marginBottom: "15px" }}>
                      {data.city || "N/A"}, {data.countryCode || "N/A"}
                    </dd>
                  </>
                )}

                {data.nameservers && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.nameservers")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.nameservers.join(", ") || "N/A"}</dd>
                  </>
                )}
              </div>

              {/* Colonna 2 */}
              <div>
                {data.ip && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.ip")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.ip}</dd>
                  </>
                )}

                <dt style={{ fontWeight: "600" }}>{t("phishing.asn")}</dt>
                <dd style={{ marginBottom: "15px" }}>
                  {data.asn || "N/A"} {data.asn_name ? `(${data.asn_name})` : ""}
                </dd>

                {data.tls?.issuer && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.tls_issuer")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.tls.issuer}</dd>
                  </>
                )}

                {data.tls?.validityFrom && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.tls_validity")}</dt>
                    <dd style={{ marginBottom: "15px" }}>
                      {moment(data.tls.validityFrom).format("DD/MM/YYYY")} -{" "}
                      {moment(data.tls.validityTo).format("DD/MM/YYYY")}
                    </dd>
                  </>
                )}

                {data.hashes?.dom && (
                  <>
                    <dt style={{ fontWeight: "600", display: "inline", marginRight: "5px" }}>
                      {t("phishing.dom_hash")}
                    </dt>
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(data.hashes.dom)}
                    >
                      <Icon fontSize="small">content_copy</Icon>
                    </IconButton>
                    <dd
                      style={{
                        marginBottom: "15px",
                        width: "100%",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {data.hashes.dom || "N/A"}
                    </dd>
                  </>
                )}

                {data.hashes?.text && (
                  <>
                    <dt style={{ fontWeight: "600", display: "inline", marginRight: "5px" }}>
                      {t("phishing.text_hash")}
                    </dt>
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(data.hashes.text)}
                    >
                      <Icon fontSize="small">content_copy</Icon>
                    </IconButton>
                    <dd
                      style={{
                        marginBottom: "15px",
                        width: "100%",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      {data.hashes.text || "N/A"}
                    </dd>
                  </>
                )}

                {data.description && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.description")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.description || "N/A"}</dd>
                  </>
                )}

                {data.intelligence && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.intelligence")}</dt>
                    <dd style={{ marginBottom: "15px" }}>{data.intelligence || "N/A"}</dd>
                  </>
                )}

                {data.faviconUrl && (
                  <>
                    <dt style={{ fontWeight: "600" }}>{t("phishing.favicon")}</dt>
                    <img src={data.faviconUrl} alt="Favicon" style={{ maxWidth: "900px" }} />
                  </>
                )}
              </div>
            </dl>

            <div>
              {data.screenshotUrl ? (
                <>
                  <dt style={{ fontWeight: "600" }}>{t("phishing.screenshot")}</dt>
                  <img src={data.screenshotUrl} alt="Screenshot" style={{ maxWidth: "900px" }} />
                </>
              ) : null}
              {screenshot ? (
                <>
                  <dt style={{ fontWeight: "600" }}>{t("phishing.screenshot")}</dt>
                  <img
                    src={URL.createObjectURL(screenshot)}
                    alt="Screenshot"
                    style={{ maxWidth: "900px" }}
                  />
                </>
              ) : null}
            </div>

            {user && user.role ? (
              <div style={{ display: "flex", gap: "50px" }}>
                {!data.managedBy ||
                (data.managedBy && data.managedBy.length < 1) ||
                (data.managedBy &&
                  data.managedBy.length > 0 &&
                  user &&
                  user.id &&
                  !data.managedBy.includes(user.id)) ? (
                  <button
                    type="button"
                    className="request-report-button"
                    onClick={() => {
                      if (data.id) {
                        phishingManaged(data.id)
                          .then((resManaged) => {
                            if (
                              resManaged &&
                              resManaged.managedBy &&
                              resManaged.managedBy.length > 0
                            ) {
                              const newPhishing = { ...data, managedBy: resManaged.managedBy };
                              setData(newPhishing);
                            }
                          })
                          .catch((errManaged) => {
                            // eslint-disable-next-line
                            console.log("error setting phishing managed", errManaged);
                          });
                      }
                    }}
                  >
                    {t("phishing.manage")}
                  </button>
                ) : (
                  <div
                    style={{
                      padding: "10px",
                      border: "1px solid green",
                      background: "#e6ffde",
                      color: "#399918",
                      borderRadius: "12px",
                      fontWeight: "600",
                    }}
                  >
                    <span>{t("phishing.managed_phishing")}</span>
                  </div>
                )}

                {data.takedownRequested ? (
                  <p>
                    {t("phishing.takedownRequested")}{" "}
                    {moment(data.takedownRequested).format("DD/MM/YYYY")}
                  </p>
                ) : (
                  <button
                    type="button"
                    className="request-report-button"
                    onClick={handleAskTakedownBtn}
                  >
                    {t("phishing.askTakedown")}
                  </button>
                )}

                {user.role === "admin" && (
                  <button
                    type="button"
                    className="request-report-button"
                    style={data.whitelisted ? { background: "#e6ffde", color: "#399918", border: "1px solid green" } : {}}
                    onClick={async () => {
                      const newValue = !data.whitelisted;
                      const result = await updateWhitelisted(data.id, newValue);
                      if (result) {
                        setData((prev) => ({ ...prev, whitelisted: newValue }));
                        setPopupSuccess(true);
                      }
                    }}
                  >
                    {data.whitelisted ? t("phishing.remove_from_whitelist") : t("phishing.add_to_whitelist")}
                  </button>
                )}

                {user.role === "admin" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ fontWeight: "600" }}>
                      {t("phishing.update_takedown_status")}
                    </label>
                    <select
                      value={data.takeDownStatus ?? PhishingTakeDownStatus.NONE}
                      onChange={async (e) => {
                        const newStatus = Number(e.target.value);
                        const result = await updateTakeDownStatus(data.id, newStatus);
                        if (result) {
                          setData((prev) => ({ ...prev, takeDownStatus: newStatus }));
                          setPopupSuccess(true);
                        }
                      }}
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.375rem",
                        borderColor: "#d2d6da",
                      }}
                    >
                      <option value={PhishingTakeDownStatus.NONE}>
                        {t("phishing.takedown_status_none")}
                      </option>
                      <option value={PhishingTakeDownStatus.REQUESTED}>
                        {t("phishing.takedown_status_requested")}
                      </option>
                      <option value={PhishingTakeDownStatus.TAKEN_DOWN}>
                        {t("phishing.takedown_status_taken_down")}
                      </option>
                      <option value={PhishingTakeDownStatus.FALSE_POSITIVE}>
                        {t("phishing.takedown_status_false_positive")}
                      </option>
                      <option value={PhishingTakeDownStatus.FAILED}>
                        {t("phishing.takedown_status_failed")}
                      </option>
                      <option value={PhishingTakeDownStatus.NOT_PROCESSABLE}>
                        {t("phishing.takedown_status_not_processable")}
                      </option>
                    </select>
                  </div>
                )}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {popupSuccess && (
        <SweetAlert
          style={{ display: "block" }}
          title={t("phishing.record_updated")}
          success
          onConfirm={() => setPopupSuccess(false)}
          confirmBtnText="Ok"
          confirmBtnCssClass="popupCloseButton"
          confirmBtnStyle={{ boxShadow: "none" }}
        >
          {t("phishing.record_updated_text")}
        </SweetAlert>
      )}
    </DashboardLayout>
  );
};

export default PhishingDetails;
